import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import styled from "styled-components";

// Styled Components
const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  height: 100%;
`;

const SearchContainer = styled.div`
  margin-bottom: 16px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
  }

  &::placeholder {
    color: #6c757d;
  }
`;

const TableContainer = styled.div`
  flex: 1;
  min-height: 300px;
  max-height: 420px;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  overflow-y: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
`;

const TableHead = styled.thead`
  background-color: #f8f9fa;
`;

const TableRow = styled.tr`
  border-bottom: 1px solid #dee2e6;

  &:hover {
    background-color: #f8f9fa;
  }
`;

const TableHeader = styled.th`
  text-align: left;
  padding: 12px;
  font-weight: 600;
  color: #495057;
  cursor: ${props => props.sortable ? 'pointer' : 'default'};

  &:hover {
    background-color: ${props => props.sortable ? '#e9ecef' : 'transparent'};
  }
`;

const TableCell = styled.td`
  padding: 12px;
  color: #495057;
  vertical-align: middle;
`;

const TableBody = styled.tbody``;

const Button = styled.button`
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 500;
  border-radius: 4px;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;

  ${props => props.variant === 'primary' ? `
    background-color: #007bff;
    color: white;
    &:hover {
      background-color: #0056b3;
    }
  ` : `
    background-color: transparent;
    color: #007bff;
    border: 1px solid #007bff;
    &:hover {
      background-color: #007bff;
      color: white;
    }
  `}

  &:disabled {
    background-color: #e9ecef;
    color: #6c757d;
    border-color: #e9ecef;
    cursor: not-allowed;
  }
`;

const Text = styled.span`
  font-size: ${props => props.size === 'small' ? '12px' : props.size === 'large' ? '16px' : '14px'};
  font-weight: ${props => props.weight === 'semibold' ? '600' : props.weight === 'bold' ? '700' : '400'};
  color: ${props => props.color || '#495057'};
  display: ${props => props.block ? 'block' : 'inline'};
`;

const ReportNameCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
  color: #6c757d;
`;

const LoadingState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: #6c757d;
`;

const SelectedInfo = styled.div`
  padding: 8px 12px;
  border-radius: 4px;
  border-left: 3px solid #007bff;
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-top: 1px solid #dee2e6;
`;

const PaginationInfo = styled.div`
  font-size: 12px;
  color: #6c757d;
`;

const PaginationButtons = styled.div`
  display: flex;
  gap: 8px;
`;

const PaginationButton = styled.button`
  padding: 4px 8px;
  font-size: 12px;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  background: ${props => props.active ? '#007bff' : 'white'};
  color: ${props => props.active ? 'white' : '#495057'};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.active ? '#0056b3' : '#f8f9fa'};
  }

  &:disabled {
    background: #e9ecef;
    color: #6c757d;
    cursor: not-allowed;
  }
`;

const ITEMS_PER_PAGE = 10;

// Helper to collect category entries from a single debrief response
const collectResponseCategories = (response) => {
  const results = [];
  const candidates = [response?.response?.categories, response?.categories];

  candidates.forEach((candidate) => {
    if (!candidate) return;
    if (Array.isArray(candidate)) {
      candidate.forEach((entry) => {
        if (entry) results.push(entry);
      });
      return;
    }

    if (typeof candidate === "object") {
      Object.values(candidate).forEach((value) => {
        if (!value) return;
        if (Array.isArray(value)) {
          value.forEach((entry) => {
            if (entry) results.push(entry);
          });
        } else if (typeof value === "object") {
          results.push(value);
        }
      });
    }
  });

  return results;
};

// Check if a single response matches the selected sport subfactor
const responseMatchesSport = (response, selectedSubfactorId) => {
  if (!selectedSubfactorId) return true;
  const categories = collectResponseCategories(response);
  return categories.some((cat) => {
    const rawSubfactor = cat?.subfactor;
    let subfactorId = null;

    if (rawSubfactor != null) {
      if (typeof rawSubfactor === "object") {
        subfactorId =
          rawSubfactor.subfactor_id ??
          rawSubfactor.id ??
          rawSubfactor.value_id ??
          rawSubfactor.value ??
          null;
      } else {
        subfactorId = rawSubfactor;
      }
    }

    // Use String() to ensure loose equality ("5" matches 5)
    const subfactorMatches =
      subfactorId != null &&
      String(subfactorId) === String(selectedSubfactorId);

    const legacyIdMatches =
      cat?.value_id != null &&
      String(cat.value_id) === String(selectedSubfactorId);
    const legacyAltIdMatches =
      cat?.id != null && String(cat.id) === String(selectedSubfactorId);
    const legacyValueMatches =
      cat?.value != null && String(cat.value) === String(selectedSubfactorId);

    return (
      subfactorMatches ||
      legacyIdMatches ||
      legacyAltIdMatches ||
      legacyValueMatches
    );
  });
};

// Check if any response within a report matches the selected sport
const reportMatchesSport = (report, selectedSubfactorId) => {
  if (!selectedSubfactorId) return true;
  const responses = report?.responses || [];
  return responses.some((resp) => responseMatchesSport(resp, selectedSubfactorId));
};

const DebriefSelector = ({
  auditReports,
  onSelect,
  selectedDebrief,
  loading,
  pulseSelectedSport,
  sportDetermined = false,
  autoSelectWhenSingle = false
}) => {
  const [searchValue, setSearchValue] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  
  // ID-based latch to prevent duplicate auto-selections
  const lastAutoSelectedId = useRef(null);
  
  const onSelectRef = useRef(onSelect);
  useEffect(() => {
    onSelectRef.current = onSelect;
  }, [onSelect]);

  // Format date helper
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Extract stable subfactor ID from pulse sport selection
  const derivedSubfactorId = useMemo(() => {
    if (pulseSelectedSport == null) {
      return null;
    } else if (typeof pulseSelectedSport === "object") {
      return (
        pulseSelectedSport.subfactor_id ??
        pulseSelectedSport.id ??
        pulseSelectedSport.value_id ??
        pulseSelectedSport.value ??
        null
      );
    }
    return pulseSelectedSport;
  }, [pulseSelectedSport]);

  // Calculate valid groups with soft filter fallback
  const validGroups = useMemo(() => {
    // Safety Checks
    if (!sportDetermined || loading || !auditReports?.response?.length) {
      return [];
    }

    const allGroups = {};
    const filteredGroups = {};
    const reports = auditReports.response;

    reports.forEach(report => {
      const name = report.name || "Unknown Report";
      
      // Accumulate ALL reports (fallback)
      if (!allGroups[name]) {
        allGroups[name] = {
          name: name,
          reports: [],
          allResponses: [],
          totalResponseCount: 0,
          latestDate: report.date,
          reportIds: []
        };
      }
      allGroups[name].reports.push(report);
      allGroups[name].reportIds.push(report.id);
      if (report.responses?.length) {
        allGroups[name].allResponses.push(...report.responses);
        allGroups[name].totalResponseCount += report.responses.length;
      }
      if (new Date(report.date) > new Date(allGroups[name].latestDate)) {
        allGroups[name].latestDate = report.date;
      }

      // Accumulate FILTERED reports (sport match)
      if (derivedSubfactorId && reportMatchesSport(report, derivedSubfactorId)) {
        if (!filteredGroups[name]) {
          filteredGroups[name] = {
            name: name,
            reports: [],
            allResponses: [],
            totalResponseCount: 0,
            latestDate: report.date,
            reportIds: []
          };
        }
        filteredGroups[name].reports.push(report);
        filteredGroups[name].reportIds.push(report.id);
        if (report.responses?.length) {
          filteredGroups[name].allResponses.push(...report.responses);
          filteredGroups[name].totalResponseCount += report.responses.length;
        }
        if (new Date(report.date) > new Date(filteredGroups[name].latestDate)) {
          filteredGroups[name].latestDate = report.date;
        }
      }
    });

    const allResults = Object.values(allGroups).filter(g => g.totalResponseCount > 0);
    const filteredResults = Object.values(filteredGroups).filter(g => g.totalResponseCount > 0);

    // Soft filtering: prefer filtered, fallback to all
    if (!derivedSubfactorId) {
      return allResults;
    }
    if (filteredResults.length > 0) {
      return filteredResults;
    }
    if (allResults.length > 0) {
      return allResults;
    }
    return [];
  }, [auditReports, derivedSubfactorId, sportDetermined, loading]);

  // Prepare table data
  const tableData = useMemo(() => {
    return validGroups
      .map(group => ({
        id: group.name,
        name: group.name,
        date: formatDate(group.latestDate),
        responses: `${group.totalResponseCount} responses`,
        rawData: group,
        dateValue: group.latestDate,
        responseCount: group.totalResponseCount
      }))
      .sort((a, b) => new Date(b.dateValue) - new Date(a.dateValue));
  }, [validGroups]);

  // Auto-selection with ID-based latching
  useEffect(() => {
    if (!autoSelectWhenSingle || validGroups.length !== 1) {
      return;
    }

    const candidate = validGroups[0];
    
    if (!candidate.reports || !candidate.reports.length) {
      return;
    }

    // Create unique signature for this data
    const candidateSignature = `${candidate.name}_${candidate.latestDate}_${candidate.totalResponseCount}`;

    // Skip if already selected this exact data
    if (lastAutoSelectedId.current === candidateSignature) {
      return;
    }

    // Execute selection
    lastAutoSelectedId.current = candidateSignature;

    const comparisonData = {
      name: candidate.name,
      reportIds: candidate.reportIds,
      reportCount: candidate.reports.length,
      latestDate: candidate.latestDate,
      allResponses: candidate.allResponses,
      totalResponseCount: candidate.totalResponseCount,
      reports: candidate.reports,
      surveyStructure: candidate.reports[0]?.question,
      surveyType: candidate.reports[0]?.question?.sort_order
    };
    
    onSelectRef.current(candidate.reports[0], comparisonData);
  }, [validGroups, autoSelectWhenSingle]);

  // UI Search filtering
  const filteredData = useMemo(() => {
    if (searchValue) {
      return tableData.filter(row => {
        const reportName = row.rawData.name?.toLowerCase() || "";
        const search = searchValue.toLowerCase();
        return reportName.includes(search);
      });
    }
    return tableData;
  }, [tableData, searchValue]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchValue]);

  // Manual selection handler
  const handleDebriefSelect = useCallback((debriefGroup) => {
    const signature = `${debriefGroup.name}_${debriefGroup.latestDate}_${debriefGroup.totalResponseCount}`;
    lastAutoSelectedId.current = signature;

    const comparisonData = {
      name: debriefGroup.name,
      reportIds: debriefGroup.reportIds,
      reportCount: debriefGroup.reports.length,
      latestDate: debriefGroup.latestDate,
      allResponses: debriefGroup.allResponses,
      totalResponseCount: debriefGroup.totalResponseCount,
      reports: debriefGroup.reports,
      surveyStructure: debriefGroup.reports[0]?.question,
      surveyType: debriefGroup.reports[0]?.question?.sort_order
    };
    
    onSelectRef.current(debriefGroup.reports[0], comparisonData);
  }, []);

  // Pagination logic
  const totalPages = Math.ceil(filteredData.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedData = filteredData.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Loading state
  if (loading || !sportDetermined) {
    return (
      <LoadingState>
        <Text>{loading ? "Loading audit reports..." : "Determining sport filter..."}</Text>
      </LoadingState>
    );
  }

  // Empty state
  if (!tableData.length) {
    return (
      <EmptyState>
        <Text size="large" weight="semibold" color="#6c757d">
          No Audit Reports with Data Found
        </Text>
        <Text color="#6c757d" style={{ marginTop: 8 }}>
          Complete a survey to generate audit reports for comparison
        </Text>
      </EmptyState>
    );
  }

  return (
    <Container>
      <SearchContainer>
        <SearchInput
          type="text"
          placeholder="Search reports..."
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
        />
      </SearchContainer>
      
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableHeader>Report Name</TableHeader>
              <TableHeader sortable>Latest Date</TableHeader>
              <TableHeader sortable>Total Responses</TableHeader>
              <TableHeader>Actions</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.map((row) => (
              <TableRow key={row.id}>
                <TableCell>
                  <ReportNameCell>
                    <Text weight="semibold">{row.name}</Text>
                  </ReportNameCell>
                </TableCell>
                <TableCell><Text color="#6c757d">{row.date}</Text></TableCell>
                <TableCell><Text color="#6c757d">{row.responses}</Text></TableCell>
                <TableCell>
                  <Button
                    variant={selectedDebrief?.name === row.rawData.name ? "primary" : "outline"}
                    onClick={() => handleDebriefSelect(row.rawData)}
                  >
                    {selectedDebrief?.name === row.rawData.name ? "Selected" : "Select"}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {totalPages > 1 && (
        <PaginationContainer>
          <PaginationInfo>
            Showing {startIndex + 1}-{Math.min(endIndex, filteredData.length)} of {filteredData.length} reports
          </PaginationInfo>
          <PaginationButtons>
            <PaginationButton
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              Previous
            </PaginationButton>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
              <PaginationButton
                key={page}
                active={page === currentPage}
                onClick={() => handlePageChange(page)}
              >
                {page}
              </PaginationButton>
            ))}
            <PaginationButton
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              Next
            </PaginationButton>
          </PaginationButtons>
        </PaginationContainer>
      )}
      
      {selectedDebrief && (
        <SelectedInfo>
          <Text size="small" color="#0c5460">
            Selected: {selectedDebrief.name || `Report #${selectedDebrief.id}`}
          </Text>
        </SelectedInfo>
      )}
    </Container>
  );
};

export default DebriefSelector;
